import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import { salesOrderAttachmentsApi } from '#/api/crmMgmt/salesOrder/attachments';
import { fetchSalesOrderDetail } from '#/api/crmMgmt/salesOrder/fetchDetail';
import { updateSalesOrder } from '#/api/crmMgmt/salesOrder/update';
import { ATTACHMENT_POLICY_SPREADSHEET } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import SalesOrderEditReferenceSections from '#/modules/crmMgmt/salesOrder/components/SalesOrderEditReferenceSections';
import SalesOrderLinesEditor from '#/modules/crmMgmt/salesOrder/components/SalesOrderLinesEditor';
import SalesOrderMainFieldsSection from '#/modules/crmMgmt/salesOrder/components/SalesOrderMainFieldsSection';
import SalesOrderShippingSection from '#/modules/crmMgmt/salesOrder/components/SalesOrderShippingSection';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import {
  getSalesOrderApiErrorMessage,
  isSalesOrderVersionConflict,
} from '#/modules/crmMgmt/salesOrder/helper/apiError';
import { buildSalesOrderWritePayload } from '#/modules/crmMgmt/salesOrder/helper/buildSalesOrderWritePayload';
import { mapSalesOrderDetailToForm } from '#/modules/crmMgmt/salesOrder/helper/mapSalesOrderDetailToForm';
import { canEditSalesOrderDraft } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import {
  SALES_ORDER_FIELD_LABELS,
  SALES_ORDER_FORM_DEFAULT_VALUES,
  resolveSalesOrderFieldLabel,
  salesOrderFormSchema,
} from '#/modules/crmMgmt/salesOrder/schemas/salesOrderSchema';
import CreateEditReferenceRail from '#/modules/infra/layout/CreateEditReferenceRail';
import PageShell from '#/modules/infra/layout/PageShell';
import { applyDocumentCurrencyFieldError } from '#/utils/documentCurrencyError';
import {
  customerDetailQueryKey,
  salesOrderDetailQueryKey,
  salesOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/**
 * 銷貨單編輯：GET detail 灌表；僅 DRAFT 可編主檔＋lines（數量／列備註）。
 * 建立請走 CreateSalesOrderPage（客訂選填／獨立 create）。
 */
const CreateEditSalesOrderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: salesOrderDetailQueryKey(id),
    queryFn: () => fetchSalesOrderDetail(id),
    enabled: Boolean(id),
  });

  const { isPending, save } = useCreateEditSave({
    id,
    createFn: async () => {
      throw new Error('銷貨單請由出貨排程建立');
    },
    updateFn: updateSalesOrder,
    buildPayload: (values) => {
      const detail = detailQuery.data?.data;
      const inherited = Boolean(detail?.customerOrderId);
      const nextId = values.currencyId || null;
      const previousId = detail?.currencyId || null;
      const currencyChanged = Boolean(nextId) && nextId !== previousId;
      return buildSalesOrderWritePayload(values, {
        updatedAt: updatedAtRef.current,
        currencyId: !inherited && currencyChanged ? nextId : undefined,
      });
    },
    listQueryKeyPrefix: salesOrderListQueryKeyPrefix,
    detailQueryKey: salesOrderDetailQueryKey,
    getErrorMessage: (error) => getSalesOrderApiErrorMessage(error, '儲存失敗'),
    onError: (error) => applyDocumentCurrencyFieldError(error, setError),
    isVersionConflict: isSalesOrderVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(salesOrderFormSchema),
    defaultValues: SALES_ORDER_FORM_DEFAULT_VALUES,
  });

  const detail = detailQuery.data?.data;
  const customerId = detail?.customerId ?? detail?.customer?.id ?? '';

  const customerDetailQuery = useQuery({
    queryKey: customerDetailQueryKey(customerId),
    queryFn: () => fetchCustomerDetail(customerId),
    enabled: Boolean(customerId) && canEditSalesOrderDraft(detail?.status),
  });

  useEffect(() => {
    if (!id) {
      navigate(ROUTES.salesOrderCreate, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    if (!canEditSalesOrderDraft(detail.status)) {
      toast.warning('僅草稿可編輯主檔，已返回詳情');
      navigate(ROUTES.salesOrderDetail(id), { replace: true });
    }
  }, [detail, id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? detail.updatedAt ?? null;
    resetValidationSummary();
    reset(mapSalesOrderDetailToForm(detail));
  }, [detail, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const isPageLoading = Boolean(id) && detailQuery.isLoading;
  const isDetailNotFound = Boolean(id) && detailQuery.isError;
  const isReadOnlyEdit = Boolean(detail) && !canEditSalesOrderDraft(detail.status);
  const canShowForm = Boolean(detail)
    && !isPageLoading
    && !isDetailNotFound
    && !isReadOnlyEdit;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <div className="create_edit_page__header">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">編輯銷貨單</span>
                {detail?.status ? (
                  <SalesOrderStatusBadge status={detail.status} size="sm" />
                ) : null}
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.salesOrder}>返回列表</Link>
              </Button>
              {detail?.customerOrderId ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.customerOrderDetail(detail.customerOrderId)}>
                    返回客訂
                  </Link>
                </Button>
              ) : null}
              {id ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.salesOrderDetail(id)}>銷貨詳情</Link>
                </Button>
              ) : null}
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getSalesOrderApiErrorMessage(detailQuery.error, '找不到銷貨單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯主檔。目前狀態為</span>
                <SalesOrderStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.salesOrderDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {canShowForm ? (
            <CreateEditReferenceRail
              className="min-h-0 flex-1"
              main={(
                <>
                  <FormValidationSummary
                    show={isSubmitAttempted}
                    errors={errors}
                    fieldLabels={SALES_ORDER_FIELD_LABELS}
                    fieldLabelResolver={resolveSalesOrderFieldLabel}
                    customMessages={customMessages}
                  />

                  <form
                    className="sheet create_edit create_edit_form create_edit_form--fill"
                    onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                    noValidate
                  >
                    <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                      <SalesOrderMainFieldsSection
                        control={control}
                        register={register}
                        errors={errors}
                        currencyLocked={Boolean(detail?.customerOrderId)}
                        savedCurrency={{
                          id: detail?.currencyId,
                          code: detail?.currencyCode,
                        }}
                      />
                      <SalesOrderShippingSection
                        control={control}
                        errors={errors}
                        addresses={customerDetailQuery.data?.data?.addresses ?? []}
                        contacts={customerDetailQuery.data?.data?.contacts ?? []}
                        snapshot={detail?.customerSnapshot}
                        isDisabled={customerDetailQuery.isLoading}
                        noCustomerMessage={
                          customerId ? '此客戶無可用地址／聯絡人' : '請先選擇客戶'
                        }
                      />
                      <SalesOrderLinesEditor
                        control={control}
                        register={register}
                        errors={errors}
                      />

                      {id ? (
                        <DocumentAttachmentsCard
                          variant="section"
                          className="pb-6"
                          businessId={String(id)}
                          ownerKey="salesOrder"
                          api={salesOrderAttachmentsApi}
                          policy={ATTACHMENT_POLICY_SPREADSHEET}
                        />
                      ) : null}
                    </div>

                    <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                      <Button type="button" variant="outline" asChild>
                        <Link to={ROUTES.salesOrder}>取消</Link>
                      </Button>
                      <Button type="submit" disabled={isPending}>
                        {isPending ? '儲存中…' : '儲存'}
                      </Button>
                    </div>
                  </form>
                </>
              )}
              reference={<SalesOrderEditReferenceSections detail={detail} />}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditSalesOrderPage;
